import { visibleFleet } from '@/lib/map/droneFilter';
import { mapAnimation } from '@/lib/map/instance';
import { PositionInterpolator } from '@/lib/map/interpolation';
import { type DroneLive, useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
/**
 * Drone DOM markers — mockup's `drawDrone()` ported to Mapbox.
 *
 * Layout: the SVG is the only in-flow child of the marker container.
 * The callsign pill (above) and status badge (below) are
 * `position:absolute`, so they do NOT contribute to the container's
 * bounding box. That keeps mapboxgl.Marker's `anchor:'center'` math
 * stable — the lat/lng point lands on the SVG's geometric center
 * regardless of which status label is rendered.
 *
 * We also UPDATE the DOM in place (innerHTML / setLngLat) instead of
 * destroying and re-creating the marker. That eliminates the visual
 * jump that was showing up during zoom / telemetry tick.
 */
import mapboxgl from 'mapbox-gl';
import type { Map as MapboxMap } from 'mapbox-gl';
import { buildDroneMarkerHtml } from './droneIcon';

// Drone telemetry @ 4 Hz → interpolate over the tick window (250 ms).
const DRONE_TICK_MS = 250;

interface ActiveDrone {
  marker: mapboxgl.Marker;
  el: HTMLElement;
  fingerprint: string;
  /** Cached SVG node so we can update rotation in O(1) without
   *  rebuilding the whole inner HTML (which restarts the CSS pulse
   *  animation and causes visible flicker). */
  svgEl: SVGSVGElement | null;
  /** Last heading we wrote to the SVG transform — avoid no-op DOM
   *  writes on every tick. */
  lastHeading: number;
}

/** Stable identity for the marker's inner HTML. Heading is deliberately
 *  NOT included — heading changes every tick and rotation is applied
 *  separately via a direct transform update, so the SVG element (and its
 *  running CSS animation) survive a heading change without restarting. */
function fingerprintOf(
  d: DroneLive,
  selected: boolean,
  hovered: boolean,
  sizeStep: number,
): string {
  return `${d.telemetry.status ?? ''}|${d.origin}|${d.partnerTenant ?? ''}|${d.telemetry.callsign}|${selected ? 1 : 0}|${hovered ? 1 : 0}|${sizeStep}`;
}

/** Apply the heading-rotation transform directly to the cached SVG node.
 *  No-op if heading hasn't changed since the last write. */
function applyHeading(active: ActiveDrone, heading: number): void {
  const rounded = Math.round(heading);
  if (rounded === active.lastHeading) return;
  active.lastHeading = rounded;
  if (active.svgEl) {
    active.svgEl.style.transform = `translate(-50%,-50%) rotate(${rounded}deg)`;
  }
}

/** Wire click/hover to the marker's visible shape (`[data-hit]`, built by
 *  `buildDroneMarkerHtml`) rather than the padded outer box — ENG-166.
 *  `el` itself is `pointer-events:none` (see the two call sites below), so a
 *  click only resolves to a drone when it lands on that drone's own painted
 *  pixels, not a neighbour's invisible padding. Re-run after every innerHTML
 *  rebuild, since that discards whichever `[data-hit]` node the previous
 *  listeners were attached to. */
function wireHitTarget(el: HTMLElement, id: string): void {
  const hit = el.querySelector<HTMLElement>('[data-hit]');
  if (!hit) return;
  hit.addEventListener('click', (e) => {
    e.stopPropagation();
    useDronesStore.getState().select(id as DroneLive['id']);
  });
  hit.addEventListener('mouseenter', () => {
    useDronesStore.getState().hover(id as DroneLive['id']);
  });
  hit.addEventListener('mouseleave', () => {
    useDronesStore.getState().hover(null);
  });
}

export function subscribeDroneMarkers(map: MapboxMap): () => void {
  const active: Record<string, ActiveDrone> = {};
  const interp = new PositionInterpolator(DRONE_TICK_MS);
  let rafId: number | null = null;
  let frameId: number | null = null;

  const sync = () => {
    const state = useDronesStore.getState();
    const ui = useUiStore.getState();
    const ctx = ui.activeContext;
    // Drones markers visible in drones-ctx always; in OI-ctx gated by showOIDrone.
    const visible = ctx === 'drones' || (ctx === 'oi' && ui.showOIDrone);
    const fleet = visible ? visibleFleet() : [];
    const seen = new Set<string>();

    for (const d of fleet) {
      const id = d.telemetry.drone_id;
      seen.add(id);
      const selected = state.selectedId === id;
      const hovered = state.hoveredId === id;
      const fp = fingerprintOf(d, selected, hovered, ui.iconSizeDrone);
      const cur = active[id];

      // Feed the interpolator the latest envelope position. The rAF loop
      // (below) reads from this and writes setLngLat at 60 fps.
      interp.target(id, d.telemetry.lng, d.telemetry.lat);

      if (!cur) {
        const el = document.createElement('div');
        const built = buildDroneMarkerHtml(
          {
            status: d.telemetry.status,
            callsign: d.telemetry.callsign,
            heading: d.telemetry.heading ?? 0,
            origin: d.origin,
            partnerTenant: d.partnerTenant,
          },
          selected,
          hovered,
          ui.iconSizeDrone,
        );
        // NOTE: do NOT set `position:relative` here. The `.mapboxgl-marker`
        // class applies `position:absolute` and we need that for the
        // marker to sit in absolute screen coordinates from Mapbox's
        // transform. `position:absolute` is still a positioning context
        // for our absolutely-positioned children (callsign pill, badge).
        //
        // `pointer-events:none` — ENG-166: this padded box is bigger than
        // the visible icon; only the icon itself (`[data-hit]`, wired below)
        // should be clickable/hoverable, or a neighbour's invisible padding
        // can sit on top of this drone's icon and steal the click.
        el.style.cssText = `width:${built.boxSize}px;height:${built.boxSize}px;pointer-events:none;z-index:${built.zIndex}`;
        el.dataset.did = id;
        el.innerHTML = built.html;
        wireHitTarget(el, id);
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([d.telemetry.lng, d.telemetry.lat])
          .addTo(map);
        const svgEl = el.querySelector('svg') as SVGSVGElement | null;
        const created: ActiveDrone = {
          marker,
          el,
          fingerprint: fp,
          svgEl,
          lastHeading: Number.NaN, // force first applyHeading to write
        };
        active[id] = created;
        applyHeading(created, d.telemetry.heading ?? 0);
        continue;
      }

      if (cur.fingerprint !== fp) {
        const rebuilt = buildDroneMarkerHtml(
          {
            status: d.telemetry.status,
            callsign: d.telemetry.callsign,
            heading: d.telemetry.heading ?? 0,
            origin: d.origin,
            partnerTenant: d.partnerTenant,
          },
          selected,
          hovered,
          ui.iconSizeDrone,
        );
        cur.el.style.width = `${rebuilt.boxSize}px`;
        cur.el.style.height = `${rebuilt.boxSize}px`;
        cur.el.style.zIndex = rebuilt.zIndex;
        cur.el.innerHTML = rebuilt.html;
        cur.fingerprint = fp;
        // innerHTML replaced the SVG node (and the `[data-hit]` element the
        // old listeners were attached to) — re-cache the SVG, force the next
        // applyHeading to write, and rewire hit-testing onto the new node.
        cur.svgEl = cur.el.querySelector('svg') as SVGSVGElement | null;
        cur.lastHeading = Number.NaN;
        wireHitTarget(cur.el, id);
      }
      // Rotate the SVG every tick (cheap; transform-only). This decouples
      // heading updates from the innerHTML rebuild path that would restart
      // the CSS pulse animation and cause visible drone-marker flicker.
      applyHeading(cur, d.telemetry.heading ?? 0);
    }

    for (const id of Object.keys(active)) {
      if (!seen.has(id)) {
        active[id]!.marker.remove();
        delete active[id];
      }
    }
    interp.retain(seen);
  };

  // Per-frame loop: read the interpolated position for every active marker
  // and write it via setLngLat. Paused while the user is animating the map
  // (mapAnimation) so live motion can't fight a user-initiated zoom/pan.
  const tick = () => {
    if (!mapAnimation.value) {
      for (const id of Object.keys(active)) {
        const [lng, lat] = interp.read(id);
        active[id]!.marker.setLngLat([lng, lat]);
      }
    }
    frameId = requestAnimationFrame(tick);
  };

  sync();
  frameId = requestAnimationFrame(tick);
  const unsubFleet = useDronesStore.subscribe(
    (s) => s.byId,
    () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        sync();
      });
    },
  );
  const unsubSel = useDronesStore.subscribe(
    (s) => s.selectedId,
    () => sync(),
  );
  const unsubHover = useDronesStore.subscribe(
    (s) => s.hoveredId,
    () => sync(),
  );
  const unsubCtx = useUiStore.subscribe(
    (s) => `${s.activeContext}-${s.droneCategory}-${s.showOIDrone}-${s.iconSizeDrone}`,
    () => sync(),
  );
  // After zoom/pan animation ends, force a sync so the marker catches up
  // to the latest lat/lng that we'd skipped during the animation.
  const unsubAnim = mapAnimation.subscribe((v) => {
    if (!v) sync();
  });

  return () => {
    unsubFleet();
    unsubSel();
    unsubHover();
    unsubCtx();
    unsubAnim();
    if (frameId !== null) cancelAnimationFrame(frameId);
    if (rafId !== null) cancelAnimationFrame(rafId);
    for (const id of Object.keys(active)) {
      active[id]!.marker.remove();
      delete active[id];
    }
  };
}
